import { withCustomBackgroundMapColorDefaults } from "@/constants/backgroundMapCustomStyle";
import {
  type BarbsIconTypeCustomOptions,
  aviationLayerConfig,
  backgroundLayerConfig,
  barbsLayerConfig,
  customGeoJsonLayerConfig,
  defaultOpacity,
  defaultTextColor,
  earthquakeLayerConfig,
  gridLayerConfig,
  isoBandsLayerConfig,
  isoLinesLayerConfig,
  jetStreamsLayerConfig,
  lightningLayerConfig,
  stationLayerConfig,
  symbolLayerConfigWWcode,
  symbolLayerConfigWeatherSymbol,
  tropicalCycloneLayerConfig,
  weatherFrontsLayerConfig,
} from "@/constants/layerConfigAttributes";
import { groundStyles } from "@/constants/maptilerStyleOptions";
import { IS_NCM } from "@/env";
import { DefaultLightningDuration, DefaultLightningRenderMode } from "@/geojson/definitions";
import type { LayerCreateUnion, LayerUnion, LayerUnionWithAll } from "@/layers";
import type { IsoBandsLayerCreate } from "@/layers/geojson/isoLayerShared";
import { DefaultSymbolIconOpacity, weatherCodeParameterName } from "@/layers/geojson/symbolLayerConstants";
import { DefaultIconData, DefaultPoiOptions, type PoiOptions } from "@/reducer/client-models";
import type { PoiLayerCreate } from "@/reducer/client-models/PoiLayer";
import type { PoiDataGrid } from "@/threads/DataProcessingThread/types";
import { getBarbsLayerParameterSettings } from "@/utility/barbsLayerOptions";
import { unsafeGetColormapByName } from "@/utility/unsafeGetColormapByName";
import { getStandardSearchEngine, getStationMosSearchEngine, getStationObsSearchEngine } from "@/weather-parameters";
import { t } from "@lingui/core/macro";
import Logger from "@mm/logging";
import {
  type AviationLayer,
  type AviationLayerCreate,
  type BackgroundMapLayer,
  type BackgroundMapLayerCreate,
  type BarbsLayerCreate,
  type CustomGeoJSONLayerCreate,
  type DrawingLayer,
  type EarthquakeLayer,
  type EarthquakeLayerCreate,
  type GridLayerCreate,
  type IsoLinesLayerCreate,
  type JetStreamsLayerCreate,
  type LightningLayerCreate,
  type NwaLayer,
  type NwaLayerCreate,
  type PressureSystemLayerCreate,
  type StationLayerCreate,
  type SymbolLayer,
  type SymbolLayerCreate,
  SymbolLayerType,
  type ThunderstormLayer,
  type ThunderstormLayerCreate,
  type TropicalCycloneLayer,
  type TropicalCycloneLayerCreate,
  type WeatherFrontsLayerCreate,
  type WindAnimationLayerCreate,
  type WmsLayerCreate,
} from "@mm/metx-workbench.meteomatics.com";
import type { WeatherParamSearchParallel } from "@mm/weather-parameter-utils/search-engines/parallel";
const logger = Logger.fromFilename(__filename);

export enum LayerKindName {
  WmsLayerDescription = "WmsLayerDescription",
  WmtsLayerDescription = "WmtsLayerDescription",
  GridLayerDescription = "GridLayerDescription",
  IsoLinesLayerDescription = "IsoLinesLayerDescription",
  IsoBandsLayerDescription = "IsoBandsLayerDescription",
  BarbsLayerDescription = "BarbsLayerDescription",
  SymbolLayerDescription = "SymbolLayerDescription",
  StationLayerDescription = "StationLayerDescription",
  BackgroundMapDescription = "BackgroundMapDescription",
  GenericPoiLayerDescription = "GenericPoiLayerDescription",
  AviationLayerDescription = "AviationLayerDescription",
  PressureSystemLayerDescription = "PressureSystemLayerDescription",
  LightningLayerDescription = "LightningLayerDescription",
  TropicalCycloneLayerDescription = "TropicalCycloneLayerDescription",
  WeatherFrontsLayerDescription = "WeatherFrontsLayerDescription",
  JetStreamsLayerDescription = "JetStreamsLayerDescription",
  WindAnimationLayerDescription = "WindAnimationLayerDescription",
  CustomGeoJSONLayerDescription = "CustomGeoJSONLayerDescription",
  DrawingLayerDescription = "DrawingLayerDescription",
  NwaLayerDescription = "NwaLayerDescription",
  EarthquakeLayerDescription = "EarthquakeLayerDescription",
  ThunderstormTrackingLayerDescription = "ThunderstormTrackingLayerDescription",
  TabLocationMarkersLayerDescription = "TabLocationMarkersLayerDescription",
}

export const nonTimeRelatedLayerKind: LayerKind[] = [
  LayerKindName.BackgroundMapDescription,
  LayerKindName.TropicalCycloneLayerDescription,
  LayerKindName.CustomGeoJSONLayerDescription,
];
export const layersWithoutTimeRounding: LayerKindName[] = [LayerKindName.LightningLayerDescription];

export type LayerKind = keyof typeof LayerKindName;

export type LayerWithParameterUnit = Exclude<
  LayerUnion,
  | BackgroundMapLayer
  | AviationLayer
  | TropicalCycloneLayer
  | DrawingLayer
  | NwaLayer
  | EarthquakeLayer
  | ThunderstormLayer
>;

export type ModeType = {
  kind: LayerKind;
  name: () => string;
  experimental?: boolean;
  productId?: "temperature-anomaly-poc";
  weatherParam?: {
    defaultSelect: string;
  };
  /**
   * Specifies whether or not the mode requires parameter search and which search engines are used for parameter search.
   * A user can choose different search options in the particular mode.
   * Provide an empty array if parameter search is unavailable for this mode.
   */
  paramSearchOptions: {
    /**
     * A label used for the user to select different search engines.
     */
    label: string;
    searchEngine: WeatherParamSearchParallel;
  }[];
};

function createStandardParamSearchOption() {
  return {
    label: "Standard",
    get searchEngine() {
      return getStandardSearchEngine();
    },
  };
}

function createStationObsParamSearchOption() {
  return {
    label: "Observations",
    get searchEngine() {
      return getStationObsSearchEngine();
    },
  };
}

function createStationMosParamSearchOption() {
  return {
    label: "MOS Forecasts",
    get searchEngine() {
      return getStationMosSearchEngine();
    },
  };
}

export const allLayerModes: ModeType[] = [
  {
    kind: LayerKindName.WmsLayerDescription,
    name: () => t`Color Maps`,
    weatherParam: {
      defaultSelect: "",
    },
    paramSearchOptions: [createStandardParamSearchOption()],
  },
  {
    kind: LayerKindName.WmsLayerDescription,
    name: () => t`Anomaly`,
    experimental: true,
    productId: "temperature-anomaly-poc",
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.GridLayerDescription,
    name: () => t`Grid`,
    weatherParam: {
      defaultSelect: "",
    },
    paramSearchOptions: [createStandardParamSearchOption()],
  },
  {
    kind: LayerKindName.IsoLinesLayerDescription,
    name: () => t`Isolines`,
    weatherParam: {
      defaultSelect: "",
    },
    paramSearchOptions: [createStandardParamSearchOption()],
  },
  {
    kind: LayerKindName.IsoBandsLayerDescription,
    name: () => t`Isobands`,
    weatherParam: {
      defaultSelect: "",
    },
    paramSearchOptions: [createStandardParamSearchOption()],
  },
  {
    kind: LayerKindName.IsoLinesLayerDescription,
    name: () => t`Isolines Ensemble`,
    experimental: true,
    weatherParam: {
      defaultSelect: "",
    },
    paramSearchOptions: [createStandardParamSearchOption()],
  },
  {
    kind: LayerKindName.SymbolLayerDescription,
    name: () => t`WW Code`,
    weatherParam: {
      defaultSelect: "weather_code_1h:idx",
    },
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.SymbolLayerDescription,
    name: () => t`Weather Symbols`,
    weatherParam: {
      defaultSelect: "weather_symbol_1h:idx",
    },
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.BarbsLayerDescription,
    name: () => t`Wind Barbs`,
    weatherParam: {
      defaultSelect: "wind_speed_10m:kn",
    },
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.BarbsLayerDescription,
    name: () => t`Wave Barbs`,
    weatherParam: {
      defaultSelect: "ocean_current_speed_10m:kn",
    },
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.WindAnimationLayerDescription,
    name: () => t`Wind Animation`,
    weatherParam: {
      // Wind animation uses wind U and V component data.
      // Since the format is the same for both wind_speed_u and wind_speed_v,
      // we only store U component and derive the V component options in layer class.
      defaultSelect: "wind_speed_u_10m:ms",
    },
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.StationLayerDescription,
    name: () => t`Station Data`,
    weatherParam: {
      defaultSelect: "",
    },
    paramSearchOptions: [createStationObsParamSearchOption(), createStationMosParamSearchOption()],
  },
  {
    kind: LayerKindName.BackgroundMapDescription,
    name: () => t`Cartographic Material`,
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.GenericPoiLayerDescription,
    name: () => t`Point Of Interest`,
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.AviationLayerDescription,
    name: () => t`Aviation`,
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.PressureSystemLayerDescription,
    weatherParam: {
      defaultSelect: "msl_pressure:hPa",
    },
    name: () => t`Pressure System`,
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.LightningLayerDescription,
    name: () => t`Lightning Data`,
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.TropicalCycloneLayerDescription,
    name: () => t`Tropical Cyclone`,
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.WeatherFrontsLayerDescription,
    name: () => t`Weather Fronts`,
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.JetStreamsLayerDescription,
    name: () => t`Jet Streams`,
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.CustomGeoJSONLayerDescription,
    name: () => t`Custom GeoJson`,
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.NwaLayerDescription,
    name: () => t`National Weather Alert`,
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.EarthquakeLayerDescription,
    name: () => t`Earthquakes`,
    paramSearchOptions: [],
  },
  {
    kind: LayerKindName.ThunderstormTrackingLayerDescription,
    name: () => t`Thunderstorm Tracking`,
    paramSearchOptions: [],
  },
];

export function createPoiLayerObject(newIdx: number, importedCoords: PoiDataGrid): LayerCreateUnion {
  const layerProps = {
    index: newIdx,
    show: true,
  };

  const poiOptions: PoiOptions = {
    hideLabels: true,
    item: {
      ...DefaultIconData,
      ...(importedCoords.length ? { coords: importedCoords } : {}),
    },
  };
  // On Poi Layer create should not be a parameter_unit
  const poiLayer: PoiLayerCreate = {
    model: "mix",
    parameter_unit: "",
    opacity: 1,
    calibrated: null,
    vertical_interpolation: null,
    ens_select: null,
    poiOptions,
    kind: LayerKindName.GenericPoiLayerDescription,
    ...layerProps,
  };
  return poiLayer;
}

function getSymbolLayerTypeFromParam(props: Pick<SymbolLayer, "parameter_unit">): SymbolLayerType {
  // Note: This function is only to be able to tell which variance of
  // symbol layer we're creating based on parameter. However, if we structure
  // AddLayer component well, we shouldn't have to do this.
  if (props.parameter_unit.startsWith(weatherCodeParameterName)) {
    return SymbolLayerType.WeatherCode;
  }
  if (props.parameter_unit.startsWith("weather_symbol_")) {
    return SymbolLayerType.WeatherSymbol;
  }

  throw Error("Unknown symbol layer variace.");
}
const PARAMETERS_VERTICAL_INTERPOLATION_NONE = ["anomaly_t_"];

export function createLayerObject(
  parameterName: { formatted: string },
  newIdx: number,
  selectedMode: ModeType,
  selectedModel: string,
): LayerCreateUnion {
  const layerProps = {
    index: newIdx,
    show: true,
  };

  switch (selectedMode.kind) {
    case LayerKindName.WmsLayerDescription: {
      // vertical_interpolation_value null means we don't set it for the api call and there it is default turned on,
      // so we explicit turn it of for some parameters
      const vertical_interpolation_value = PARAMETERS_VERTICAL_INTERPOLATION_NONE.some((value) =>
        parameterName.formatted.startsWith(value),
      )
        ? "none"
        : null;

      const wmsLayer: WmsLayerCreate = {
        calibrated: false,
        ens_select: null,
        experimental: false,
        show_init_time: null,
        vertical_interpolation: vertical_interpolation_value,
        model: selectedModel,
        parameter_unit: parameterName.formatted,
        color_map: unsafeGetColormapByName("", parameterName.formatted),
        opacity: defaultOpacity,
        legend_visible: true,
        custom_options: {},
        ...layerProps,
        kind: selectedMode.kind,
      };
      return wmsLayer;
    }
    case LayerKindName.GridLayerDescription: {
      const gridLayer: GridLayerCreate = {
        calibrated: false,
        ens_select: null,
        experimental: false,
        kind: selectedMode.kind,
        show_init_time: undefined,
        vertical_interpolation: null,
        model: selectedModel,
        parameter_unit: parameterName.formatted,
        text_size: gridLayerConfig.defaultTextSize,
        text_color: defaultTextColor,
        step: gridLayerConfig.defaultStep,
        opacity: defaultOpacity,
        ...layerProps,
      };
      return gridLayer;
    }
    case LayerKindName.IsoLinesLayerDescription: {
      const isoLinesLayer: IsoLinesLayerCreate = {
        calibrated: false,
        ens_select: null,
        vertical_interpolation: null,
        model: selectedModel,
        parameter_unit: parameterName.formatted,
        text_color: defaultTextColor,
        text_size: isoLinesLayerConfig.defaultTextSize,
        line_width: isoLinesLayerConfig.defaultIsoLineWidth,
        line_color: isoLinesLayerConfig.defaultLineColor,
        opacity: defaultOpacity,
        kind: selectedMode.kind,
        filter_gauss: isoLinesLayerConfig.defaultGaussian,
        filter_median: isoLinesLayerConfig.defaultMedian,
        // If Iso Line is flagged as experimental, vector tile layer will be used
        experimental: selectedMode.experimental,
        // values: '-10,0,5,10,15', // Both values and value_range are optional parameters.
        // value_range: "-10,20,2",
        ...layerProps,
      };
      return isoLinesLayer;
    }
    case LayerKindName.IsoBandsLayerDescription: {
      const isoBandsLayer: IsoBandsLayerCreate = {
        calibrated: false,
        ens_select: null,
        vertical_interpolation: null,
        model: selectedModel,
        parameter_unit: parameterName.formatted,
        text_color: defaultTextColor,
        text_size: isoLinesLayerConfig.defaultTextSize,
        line_width: isoLinesLayerConfig.defaultIsoLineWidth,
        line_color: isoLinesLayerConfig.defaultLineColor,
        opacity: defaultOpacity,
        kind: selectedMode.kind,
        filter_gauss: isoLinesLayerConfig.defaultGaussian,
        filter_median: isoLinesLayerConfig.defaultMedian,
        custom_options: {
          fill_color: isoBandsLayerConfig.defaultFillColor,
        },
        experimental: selectedMode.experimental ?? false,
        ...layerProps,
      };
      return isoBandsLayer;
    }
    case LayerKindName.WeatherFrontsLayerDescription: {
      const weatherFrontsLayer: WeatherFrontsLayerCreate = {
        calibrated: false,
        ens_select: null,
        vertical_interpolation: null,
        model: selectedModel,
        parameter_unit: "",
        text_size: weatherFrontsLayerConfig.iconDefaultSize,
        line_width: weatherFrontsLayerConfig.defaultLineWidth,
        opacity: 1,
        kind: selectedMode.kind,
        // If Iso Line is flagged as experimental, vector tile layer will be used
        experimental: selectedMode.experimental,
        ...layerProps,
      };
      return weatherFrontsLayer;
    }
    case LayerKindName.JetStreamsLayerDescription: {
      const jetStreamsLayer: JetStreamsLayerCreate = {
        calibrated: false,
        ens_select: null,
        vertical_interpolation: null,
        model: selectedModel || "mix",
        parameter_unit: "",
        line_width: jetStreamsLayerConfig.defaultLineWidth,
        legend_visible: true,
        custom_options: {
          show_arrows: jetStreamsLayerConfig.defaultShowArrows,
          arrow_size: jetStreamsLayerConfig.defaultArrowSize,
        },
        opacity: 1,
        kind: selectedMode.kind,
        ...layerProps,
      };
      return jetStreamsLayer;
    }
    case LayerKindName.BarbsLayerDescription: {
      const { parameter_unit_paired, custom_options } = getBarbsLayerParameterSettings(parameterName.formatted, {
        icon_type: barbsLayerConfig.defaultIconType,
        icon_size: barbsLayerConfig.defaultIconSize,
      });
      if (!parameter_unit_paired) {
        logger.error(`Could not replace speed with direction from parameter: ${parameterName.formatted}`);
      }

      const barbsLayer: BarbsLayerCreate = {
        calibrated: false,
        ens_select: null,
        vertical_interpolation: null,
        model: selectedModel,
        parameter_unit: parameterName.formatted,
        parameter_unit_paired,
        opacity: defaultOpacity,
        step: barbsLayerConfig.defaultStep,
        element_color: defaultTextColor,
        kind: selectedMode.kind,
        custom_options: custom_options as BarbsLayerCreate["custom_options"] & BarbsIconTypeCustomOptions,
        ...layerProps,
      };
      return barbsLayer;
    }
    case LayerKindName.SymbolLayerDescription: {
      const symbolLayerType = getSymbolLayerTypeFromParam({ parameter_unit: parameterName.formatted });
      const symbolLayer: SymbolLayerCreate = {
        ens_select: null,
        vertical_interpolation: null,
        calibrated: false,
        model: selectedModel,
        parameter_unit: parameterName.formatted,
        opacity: DefaultSymbolIconOpacity,
        layer_type: symbolLayerType, // (parameterName.formatted) has ww_code or weather_code then "WeatherCode" else "WeatherSymbol"
        kind: selectedMode.kind,
        step:
          symbolLayerType === SymbolLayerType.WeatherSymbol
            ? symbolLayerConfigWeatherSymbol.defaultStep
            : symbolLayerConfigWWcode.defaultStep,
        custom_options: {
          icon_size:
            symbolLayerType === SymbolLayerType.WeatherSymbol
              ? symbolLayerConfigWeatherSymbol.defaultIconSize
              : symbolLayerConfigWWcode.defaultIconSize,
        },
        ...layerProps,
      };
      return symbolLayer;
    }
    case LayerKindName.BackgroundMapDescription: {
      const backgroundMapLayer: BackgroundMapLayerCreate = {
        calibrated: false,
        kind: "BackgroundMapDescription",
        vertical_interpolation: null,
        opacity: defaultOpacity,
        style: groundStyles[0].id,
        custom_options: withCustomBackgroundMapColorDefaults(groundStyles[0].id, {}),
        ...layerProps,
      };
      return backgroundMapLayer;
    }
    case LayerKindName.StationLayerDescription: {
      const stationLayer: StationLayerCreate = {
        calibrated: false,
        ens_select: null,
        kind: "StationLayerDescription",
        vertical_interpolation: null,
        model: selectedModel,
        parameter_unit: parameterName.formatted,
        text_size: stationLayerConfig.defaultTextSize,
        text_color: defaultTextColor,
        ...layerProps,
      };
      return stationLayer;
    }
    case LayerKindName.GenericPoiLayerDescription: {
      const poiOptions: PoiOptions = { ...DefaultPoiOptions };
      const poiLayer: PoiLayerCreate = {
        calibrated: false,
        ens_select: null,
        kind: LayerKindName.GenericPoiLayerDescription,
        vertical_interpolation: null,
        model: "mix",
        parameter_unit: "t_2m:C",
        opacity: 1,
        poiOptions,
        ...layerProps,
      };
      return poiLayer;
    }
    case LayerKindName.AviationLayerDescription: {
      const aviationLayer: AviationLayerCreate = {
        calibrated: false,
        kind: "AviationLayerDescription",
        vertical_interpolation: null,
        aviation_type: "metar",
        opacity: 1,
        text_size: aviationLayerConfig.defaultSize,
        custom_options: { show_timed_out: aviationLayerConfig.defaultMetarShowTimedOut },
        ...layerProps,
      };

      return aviationLayer;
    }
    case LayerKindName.PressureSystemLayerDescription: {
      const pressureSystemLayer: PressureSystemLayerCreate = {
        calibrated: false,
        ens_select: null,
        kind: "PressureSystemLayerDescription",
        vertical_interpolation: null,
        model: selectedModel,
        parameter_unit: parameterName.formatted,
        text_color: defaultTextColor,
        text_size: isoLinesLayerConfig.defaultTextSize,
        line_width: isoLinesLayerConfig.defaultIsoLineWidth,
        line_color: isoLinesLayerConfig.defaultLineColor,
        opacity: defaultOpacity,
        filter_gauss: isoLinesLayerConfig.defaultGaussian,
        filter_median: isoLinesLayerConfig.defaultMedian,
        value_range: "950,1050,4",
        ...layerProps,
      };
      return pressureSystemLayer;
    }
    case LayerKindName.LightningLayerDescription: {
      const lightningLayer: LightningLayerCreate = {
        calibrated: false,
        vertical_interpolation: null,
        ens_select: null,
        kind: "LightningLayerDescription",
        model: selectedModel,
        parameter_unit: DefaultLightningDuration,
        text_size: lightningLayerConfig.defaultTextSize,
        text_color: lightningLayerConfig.defaultColor,
        custom_options: {
          clustering_type: DefaultLightningRenderMode,
        },
        ...layerProps,
      };
      return lightningLayer;
    }
    case LayerKindName.TropicalCycloneLayerDescription: {
      const tropicalCycloneLayer: TropicalCycloneLayerCreate = {
        calibrated: false,
        kind: "TropicalCycloneLayerDescription",
        vertical_interpolation: null,
        aviation_type: "",
        line_width: tropicalCycloneLayerConfig.defaultLineWidth,
        line_color: tropicalCycloneLayerConfig.defaultLineColor,
        ...layerProps,
      };
      return tropicalCycloneLayer;
    }

    case LayerKindName.WindAnimationLayerDescription: {
      const windAnimationLayer: WindAnimationLayerCreate = {
        ens_select: null,
        kind: "WindAnimationLayerDescription",
        calibrated: false,
        vertical_interpolation: null,
        model: selectedModel,
        parameter_unit: parameterName.formatted,
        color_map: IS_NCM ? "wind_arabia" : "jet",
        opacity: 1,
        ...layerProps,
      };
      return windAnimationLayer;
    }
    case LayerKindName.CustomGeoJSONLayerDescription: {
      const customGeoJsonLayer: CustomGeoJSONLayerCreate = {
        ens_select: null,
        kind: "CustomGeoJSONLayerDescription",
        legend_visible: null,
        model: null,
        parameter_unit: null,
        calibrated: false,
        vertical_interpolation: null,
        custom_options: {
          line_color: backgroundLayerConfig.defaultLineColor,
          line_width: customGeoJsonLayerConfig.defaultLineWidth,
        },
        file_id: null,
        user_file_id: null,
        opacity: 1,
        ...layerProps,
      };
      return customGeoJsonLayer;
    }
    case LayerKindName.NwaLayerDescription: {
      const nwaLayer: NwaLayerCreate = {
        calibrated: false,
        kind: "NwaLayerDescription",
        vertical_interpolation: null,
        opacity: defaultOpacity,
        // No model/parameter_unit for NWA; only custom options (currently optional filter only)
        text_size: null,
        custom_options: {},
        ...layerProps,
      };
      return nwaLayer;
    }
    case LayerKindName.EarthquakeLayerDescription: {
      const earthquakeLayer: EarthquakeLayerCreate = {
        element_color: earthquakeLayerConfig.defaultElementColor,
        kind: "EarthquakeLayerDescription",
        calibrated: false,
        vertical_interpolation: null,
        opacity: defaultOpacity,
        // No model/parameter_unit for NWA; only custom options (currently optional filter only)
        // text_size: null,
        custom_options: {
          time_duration: earthquakeLayerConfig.defaultEarthquakeLayerTimeDuration,
          icon_size: earthquakeLayerConfig.defaultElementSize,
          clustering: earthquakeLayerConfig.defaultEarthquakeLayerClustering,
        },
        ...layerProps,
      };
      return earthquakeLayer;
    }
    case LayerKindName.ThunderstormTrackingLayerDescription: {
      const thunderstormLayer: ThunderstormLayerCreate = {
        kind: "ThunderstormTrackingLayerDescription",
        calibrated: false,
        vertical_interpolation: null,
        opacity: defaultOpacity,
        custom_options: {},
        ...layerProps,
      };
      return thunderstormLayer;
    }
    default: {
      throw new Error(`Unhandled kind: ${selectedMode.kind}`);
    }
  }
}
